import { ArrowRight, ChevronDown } from 'lucide-react';

const APP_URL = 'https://console.filemingle.com';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-hero-gradient">
      {/* Floating gradient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-brand-blue opacity-20 blur-3xl animate-float" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-indigo opacity-25 blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[360px] h-[360px] rounded-full bg-blue-500 opacity-10 blur-3xl animate-float" />
      </div>

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <div className="relative">
            <div className="absolute inset-0 bg-brand-blue rounded-2xl blur-xl opacity-40 scale-110" />
            <div className="relative glass rounded-2xl p-3">
              <img
                src="/assets/logo_large.png"
                alt="FileMingle"
                className="h-16 w-16 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Badge */}
        <div className="flex justify-center mb-6 animate-fade-in">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-white/80 text-xs font-semibold tracking-wider uppercase">
            All-in-One File Utility Platform
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-white leading-[1.08] tracking-tight mb-6 animate-fade-in-up">
          Smart File Tools,{' '}
          <span className="text-gradient-light">All in One Place</span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-lg sm:text-xl text-white/65 max-w-2xl mx-auto leading-relaxed mb-10 animate-fade-in-up"
          style={{ animationDelay: '0.1s', opacity: 0 }}
        >
          Create fillable PDFs, convert images, compress files, and manage
          documents effortlessly all directly in your browser.
        </p>

        {/* CTA buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up"
          style={{ animationDelay: '0.2s', opacity: 0 }}
        >
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-brand-blue font-bold text-base shadow-2xl shadow-black/30 hover:shadow-white/20 hover:scale-105 transition-all duration-250"
          >
            Launch App
            <ArrowRight
              size={18}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
          <a
            href="#features"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass text-white font-semibold text-base hover:bg-white/15 transition-all duration-200"
          >
            Learn More
            <ChevronDown size={18} className="animate-bounce" />
          </a>
        </div>

        {/* Social proof strip */}
        <div
          className="mt-16 flex flex-wrap items-center justify-center gap-6 text-white/40 text-sm animate-fade-in"
          style={{ animationDelay: '0.4s', opacity: 0 }}
        >
          {['PDF Tools', 'Image Conversion', 'Compression', 'Browser-Based', 'No Install'].map(
            (tag) => (
              <span key={tag} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-white/30" />
                {tag}
              </span>
            )
          )}
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <a
        href="#features"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/30 hover:text-white/60 transition-colors duration-200"
      >
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <ChevronDown size={18} className="animate-bounce" />
      </a>
    </section>
  );
}
